// Serves as a wrapper to our pages, 
// this basically supplies if we are authenticated or not to the rest of the program

import { useNavigate, useLocation } from "react-router-dom";
import { useDispatch } from 'react-redux';
import { signOutUser, userStateListener, signInUser, signUpUser, sendUserVerificationEmail } from "../firebase/firebase";
import { createContext, useContext, useState, useEffect, useRef } from "react";
import { message } from "antd";
import {
    AUTH_TOKEN,
    Coinbase_Auth_Token,
    Coinbase_Refresh_Token,
    LAST_VISITED_LOCATION,
    IS_ADMIN
  } from "../const/const";
import { registerAuthorizationHeader } from "../environment";
import { getUser } from '../rest/user';
import { getCryptoAccount } from "../store/actions/cryptoAccount.action";
import { persistor } from '../store/store';
import { getCurrentLegalDocument, saveUserLegalDocumentSignedOn } from '../rest/legal-document';
import { getMaintenanceSetting } from '../rest/maintenance';

const storage = localStorage;

export const AuthContext = createContext({
  authenticating: true,
  legalDocSigned: false,
  setLegalDocSigned: () => {},
  saveLegalDocSigned: async () => {},
  currentUser: {},
  setCurrentUser: () => {},
  signOut: () => {},
  signIn: () => {},
  signUp: () => {}
});

export const AuthProvider = ({ children }) => {
    const location = useLocation();
    // currentUser for MongoDB user object
    const [currentUser, setCurrentUser] = useState(null);
    // loginDetails for firebase Login response
    const [loginDetails, setLoginDetails] = useState(null); 
    const [authenticating, setAuthenticating] = useState(true);
    const [userIsVerified, setUserIsVerified] = useState(null);
    const [legalDocSigned, setLegalDocSigned] = useState(null); // null, false, true
    const [userRole, setUserRole] = useState(null);
    const [maintenanceHeadsupMessage, setMaintenanceHeadsupMessage] = useState(null);
    const [maintenanceMode, setMaintenanceMode] = useState(null);
    const maintenanceIntervalRef = useRef(null);

    const navigate = useNavigate();
    const dispatch = useDispatch();

  // As soon as setting the current user to null, 
  // the user will be redirected to the home page. 
  const signOut = () => {
    signOutUser();
    setLoginDetails(null);
    setCurrentUser(null);
    storage.removeItem(Coinbase_Auth_Token);
    storage.removeItem(Coinbase_Refresh_Token);
    storage.removeItem(AUTH_TOKEN);
    storage.removeItem(LAST_VISITED_LOCATION);
    persistor.purge();

    const isAdmin = storage.getItem(IS_ADMIN);
    if (isAdmin) {
      storage.removeItem(IS_ADMIN);
    }
    
    if (
      location.pathname !== '/login' &&
      location.pathname !== '/register' &&
      location.pathname !== '/admin') {
        navigate(isAdmin === 'true' ? '/admin' : '/');
      }
  }
  // GK 2023-10-22 - what happens when you sign in basically - you get the user, get the crypto account info
  const fetchUser = async (isRedirect = true) => {
    try {
      const currentLegalDocumentResponse = await getCurrentLegalDocument();
      const userResponse = await getUser();
      dispatch(getCryptoAccount());
      
      const defaultRedirectUrl = location.pathname.startsWith('/admin') ? '/admin/dashboard' : '/transactions';
      const lastVisitedLocation = storage.getItem(LAST_VISITED_LOCATION);
      const redirectUrl = lastVisitedLocation ? lastVisitedLocation : defaultRedirectUrl;

      // if(!userRole && userResponse.data?.role) {  // set userRole if not set in userStateListener       
      //   setUserRole(userResponse.data.role.toLowerCase());
      // }
      console.log('userResponse --->', userResponse, 'userRole ==>', userRole);
      if (location.pathname.startsWith('/admin')) {
        if (userRole === 'admin' && userResponse?.data?.role?.toLowerCase() === 'admin') {
          storage.setItem(IS_ADMIN, 'true');
        } else {
          message.error('This account is not an admin or not properly setup to be an admin.', undefined, () => signOut());
          return;
        }      
      }
      
      if (
        userResponse?.data?.role?.toLowerCase() === 'admin' ||
        (
          currentLegalDocumentResponse.data.legalDocNames &&
          userResponse.data.legalDocument?.name_of_documents === currentLegalDocumentResponse.data.legalDocNames
        )
      ) {
        setLegalDocSigned(true);
      } else {
        setLegalDocSigned(false);
      }

      setCurrentUser(userResponse.data);
      
      if(isRedirect) {
        navigate(redirectUrl);
      } else {
        return {
          legalDocSigned: userResponse.data.legalDocument?.name_of_documents === currentLegalDocumentResponse.data.legalDocNames,
          currentUser: userResponse.data
        }
      }
    } catch (error) {
      console.log('FetchUser Error =>', error);


      // GK 2025-02-12 - This is what I'm adding to help deal with tokens expiring
      // If the error response indicates an expired token, sign out the user
      if (error.response && error.response.data.errorCode === 'TOKEN_EXPIRED') {
        signOut();
      }
    }
  }

  const saveLegalDocSigned = async () => {
    try {
      await saveUserLegalDocumentSignedOn('web');

      return fetchUser(false);
    } catch (error) {
      if (error.response && error.response.data.errorCode === 'TOKEN_EXPIRED') {
        signOut();
      }

      throw error;
    }
  }

  const handleMaintenanceSetting = () => {
    // Fetch maintenance setting immediately
    getMaintenanceSetting()
      .then(response => {
        setMaintenanceMode(response.data?.mode); // always set the maintenanceMode.

        if ((response.data?.headsup === 'on' || response.data?.mode === 'on') && !!response.data?.headsupMessage) {
          setMaintenanceHeadsupMessage(response.data.headsupMessage);          
        } else {
          setMaintenanceHeadsupMessage(null);
        }
      });
  }

  // redirect AFTER current user is set
  useEffect(() => {
    if(!loginDetails) {
        return;
    }

    // if the user has not verified via email, the user should be logged out
    if(!loginDetails?.emailVerified) {
        signOut();
        return;
    }

    // if the user is logged in, then we should run the fetchUser() function
    if(loginDetails && registerAuthorizationHeader(loginDetails.accessToken)) {
        fetchUser();       
    }
  }, [loginDetails]);


  useEffect(() => {
    //Subscribe to firebase authentication events like login , logout etc
    const unsubscribe = userStateListener(async (loginResponse) => {
      if (loginResponse) {
        const tokenResult = await loginResponse.getIdTokenResult(true);
        
        if(tokenResult?.claims?.role) {
          setUserRole(tokenResult?.claims?.role);
        }
        console.log('firebase loginResponse ===> ', loginResponse);
        storage.setItem(AUTH_TOKEN, loginResponse.accessToken);        
        setUserIsVerified(loginResponse.emailVerified);
        setLoginDetails(loginResponse);
        
      } else {
        setLoginDetails(null);
      }
      setAuthenticating(false);
    });

  // GK 2023-10-22 - Any time the auth context is destroyed, unsubscribe from the userStateListener event
  return unsubscribe;
  }, [setLoginDetails]);

  useEffect(() => {
    if (loginDetails) {
      handleMaintenanceSetting();

      // run on every 10 minute
      maintenanceIntervalRef.current = setInterval(() => handleMaintenanceSetting(), 1000 * 30 /* 1000 * 60*10 /* every 10 minute */);
    }

    return () => {
      if (maintenanceIntervalRef.current) clearInterval(maintenanceIntervalRef.current);
    }
  }, [loginDetails])

  const value = {
    authenticating,
    legalDocSigned,
    setLegalDocSigned,
    saveLegalDocSigned,
    loginDetails, // Login details from Firebase,
    setLoginDetails,
    currentUser, // User object from Mongo Backeend
    setCurrentUser,
    userRole,
    userIsVerified, // Indicates if user has complete email verification or not
    sendUserVerificationEmail,
    maintenanceHeadsupMessage,
    maintenanceMode,
    signOut,
    signIn: signInUser,
    signUp: signUpUser,
  }

  // GK 2023-10-22 - provides the context for authentication
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// Custom hook to access our firebase auth information
export const useAuth = () => useContext(AuthContext);